import { useCallback, useEffect, useMemo, useState } from "react";
import { SpendCategory, VendorOrderForm } from "../../../types/VendorTypes";
import { getVendorCategories, getVendorOrderForms } from "../../../common/api/VendorClient";
import { useOrgCategories } from "../../../common/useOrgCategories";
import { handleError } from "../../../common/ErrorHandling";
import { buildVendorSpend, VendorSpendModel } from "./vendorSpend";

/**
 * The spend model behind the vendor pages: every vendor with its deals, each
 * deal under its category, ordered by the org's own taxonomy when it has one
 * and by the IL spend categories otherwise. Undefined while loading.
 */
export function useVendorSpend(): { model: VendorSpendModel | undefined; reload: () => void } {
    const orgCategories = useOrgCategories();
    const [ilCategories, setIlCategories] = useState<SpendCategory[]>();
    const [vendors, setVendors] = useState<VendorOrderForm[]>();

    const reload = useCallback(() => {
        getVendorOrderForms()
            .then((response) => setVendors(response.data))
            .catch(handleError);
    }, []);

    useEffect(reload, [reload]);

    // The IL taxonomy only matters for orgs without their own.
    useEffect(() => {
        if (orgCategories === null) getVendorCategories().then(setIlCategories).catch(handleError);
    }, [orgCategories]);

    const taxonomy = orgCategories ?? ilCategories;
    const model = useMemo(
        () => vendors && taxonomy && buildVendorSpend(vendors, taxonomy),
        [vendors, taxonomy]
    );

    return { model, reload };
}
